import React, { useState, useEffect } from 'react';
import { LocalMatch, LocalMatchStatus, getLocalMatchStatusInfo, formatDateTime, formatLocalMatchResult, formatLocalMatchTeams, formatDate, LocalMatchday, CreateLocalMatchRequest, UpdateLocalMatchRequest } from '../types/localMatchday';
import { LocalMatchApi, LocalMatchdayApi } from '../services/localMatchdayApi';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import LocalMatchForm from '../components/localMatch/LocalMatchForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { CircleDot, Plus, LayoutGrid, List, Building2, CalendarDays, CheckCircle, MapPin, User, Clock, Trophy, Pencil, Pause, Play, Trash2 } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

const LocalMatches: React.FC = () => {
  const [localMatches, setLocalMatches] = useState<LocalMatch[]>([]);
  const [localMatchdays, setLocalMatchdays] = useState<LocalMatchday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'cards' | 'list'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showMatchForm, setShowMatchForm] = useState(false);
  const [editingMatch, setEditingMatch] = useState<LocalMatch | null>(null);
  const [matchToDelete, setMatchToDelete] = useState<LocalMatch | null>(null);
  const [matchFormLoading, setMatchFormLoading] = useState(false);

  const { toasts, removeToast, showError, showSuccess } = useToast();

  useEffect(() => {
    loadLocalMatches();
    loadLocalMatchdays();
  }, [showInactive]);

  const loadLocalMatches = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await LocalMatchApi.getAllLocalMatches(true); // Always include inactive for management
      setLocalMatches(data);
    } catch (err) {
      setError('Fehler beim Laden der lokalen Spiele');
      console.error('Error loading local matches:', err);
      showError('Fehler beim Laden der lokalen Spiele');
    } finally {
      setLoading(false);
    }
  };

  const loadLocalMatchdays = async () => {
    try {
      const matchdays = await LocalMatchdayApi.getAllLocalMatchdays(true); // Include inactive
      setLocalMatchdays(matchdays);
    } catch (error) {
      console.error('Error loading local matchdays:', error);
      showError('Fehler beim Laden der Spieltage');
    }
  };

  // Match management functions
  const handleAddMatch = () => {
    setEditingMatch(null);
    setShowMatchForm(true);
  };

  const handleEditMatch = (match: LocalMatch) => {
    setEditingMatch(match);
    setShowMatchForm(true);
  };

  const handleCreateMatch = async (request: CreateLocalMatchRequest) => {
    try {
      setMatchFormLoading(true);
      await LocalMatchApi.createLocalMatch(request);
      await loadLocalMatches();
      setShowMatchForm(false);
      showSuccess('Spiel erfolgreich erstellt');
    } catch (error) {
      showError('Fehler beim Erstellen des Spiels');
    } finally {
      setMatchFormLoading(false);
    }
  };

  const handleUpdateMatch = async (request: UpdateLocalMatchRequest) => {
    if (!editingMatch) return;
    try {
      setMatchFormLoading(true);
      await LocalMatchApi.updateLocalMatch(editingMatch.id, request);
      await loadLocalMatches();
      setShowMatchForm(false);
      setEditingMatch(null);
      showSuccess('Spiel erfolgreich aktualisiert');
    } catch (error) {
      showError('Fehler beim Aktualisieren des Spiels');
    } finally {
      setMatchFormLoading(false);
    }
  };

  const handleSaveMatch = async (data: CreateLocalMatchRequest | UpdateLocalMatchRequest) => {
    if (editingMatch) {
      // For updates, we don't need localMatchdayId
      const updateData: UpdateLocalMatchRequest = {
        homeTeam: data.homeTeam,
        awayTeam: data.awayTeam,
        scheduledDate: data.scheduledDate,
        actualDate: data.actualDate,
        location: data.location,
        referee: data.referee,
        duration: data.duration,
        notes: data.notes,
        status: data.status,
        homeScore: data.homeScore,
        awayScore: data.awayScore,
        isActive: data.isActive
      };
      await handleUpdateMatch(updateData);
    } else {
      // For creates, we need localMatchdayId
      await handleCreateMatch(data as CreateLocalMatchRequest);
    }
  };

  const handleToggleMatchActive = async (match: LocalMatch) => {
    try {
      await LocalMatchApi.toggleLocalMatchActive(match.id);
      await loadLocalMatches();
      showSuccess(`Spiel erfolgreich ${match.isActive ? 'deaktiviert' : 'aktiviert'}`);
    } catch (error) {
      console.error('Error toggling match status:', error);
      showError('Fehler beim Ändern des Spiel-Status');
    }
  };

  const handleDeleteMatch = async () => {
    if (!matchToDelete) return;
    try {
      await LocalMatchApi.deleteLocalMatch(matchToDelete.id);
      await loadLocalMatches();
      setMatchToDelete(null);
      showSuccess('Spiel erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting match:', error);
      showError('Fehler beim Löschen des Spiels');
    }
  };

  const filteredLocalMatches = localMatches.filter(localMatch => {
    const matchesSearch = searchTerm === '' || 
      localMatch.homeTeam.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localMatch.awayTeam.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localMatch.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localMatch.referee?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localMatch.localMatchday.name.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const stats = {
    total: filteredLocalMatches.length,
    active: filteredLocalMatches.filter(lm => lm.isActive).length,
    inactive: filteredLocalMatches.filter(lm => !lm.isActive).length,
    scheduled: filteredLocalMatches.filter(lm => lm.status === LocalMatchStatus.Scheduled).length,
    live: filteredLocalMatches.filter(lm => lm.status === LocalMatchStatus.Live).length,
    finished: filteredLocalMatches.filter(lm => lm.status === LocalMatchStatus.Finished).length
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">Lade lokale Spiele...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={handleAddMatch} title="Neues Spiel hinzufügen">
            <Plus size={14} /> Neu
          </button>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Inaktive anzeigen
          </label>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Suchen nach Teams, Ort, Schiedsrichter..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{stats.total} Gefunden · {stats.active} Aktiv · {stats.scheduled} Geplant · {stats.live} Live · {stats.finished} Beendet</span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="page-error">
          <p>{error}</p>
          <button onClick={loadLocalMatches}>Erneut versuchen</button>
        </div>
      )}

      {/* Content */}
      {filteredLocalMatches.length === 0 ? (
        <div className="page-empty-state">
          <CircleDot size={32} />
          <h3>Keine lokalen Spiele gefunden</h3>
          <p>Es wurden noch keine lokalen Spiele erstellt oder sie entsprechen nicht den Suchkriterien.</p>
        </div>
      ) : viewMode === 'cards' ? (
        <div className="page-cards-grid">
          {filteredLocalMatches.map((localMatch) => {
            const statusInfo = getLocalMatchStatusInfo(localMatch.status);
            return (
              <div key={localMatch.id} className="page-card">
                <div className="page-card-header">
                  <h3><CircleDot size={14} /> {formatLocalMatchTeams(localMatch)}</h3>
                  <div className="page-card-badges">
                    <span className="page-badge" style={{ backgroundColor: statusInfo.color }}>
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                    {!localMatch.isActive && (
                      <span className="page-badge" style={{ backgroundColor: '#dc3545' }}>Inaktiv</span>
                    )}
                  </div>
                </div>

                <div className="page-card-body">
                  <div><strong>{localMatch.homeTeam}</strong> vs <strong>{localMatch.awayTeam}</strong></div>
                  <div><strong>Ergebnis:</strong> {formatLocalMatchResult(localMatch)}</div>
                  <div><Building2 size={12} /> <strong>Spieltag:</strong> {localMatch.localMatchday.name}</div>
                  <div><CalendarDays size={12} /> <strong>Geplant:</strong> {formatDateTime(localMatch.scheduledDate)}</div>
                  {localMatch.actualDate && (
                    <div><CheckCircle size={12} /> <strong>Tatsächlich:</strong> {formatDateTime(localMatch.actualDate)}</div>
                  )}
                  {localMatch.location && (
                    <div><MapPin size={12} /> <strong>Ort:</strong> {localMatch.location}</div>
                  )}
                  {localMatch.referee && (
                    <div><User size={12} /> <strong>Schiedsrichter:</strong> {localMatch.referee}</div>
                  )}
                  {localMatch.duration && (
                    <div><Clock size={12} /> <strong>Dauer:</strong> {localMatch.duration} Min.</div>
                  )}
                  {localMatch.winner !== 'TBD' && localMatch.status === LocalMatchStatus.Finished && (
                    <div><Trophy size={12} /> <strong>Gewinner:</strong> {localMatch.winner}</div>
                  )}
                  {localMatch.notes && <p><strong>Notizen:</strong> {localMatch.notes}</p>}
                </div>

                <div className="page-card-footer">
                  <span></span>
                  <div className="page-card-actions">
                    <button className="page-btn-icon edit" onClick={() => handleEditMatch(localMatch)} title="Bearbeiten"><Pencil size={14} /></button>
                    <button className="page-btn-icon" onClick={() => handleToggleMatchActive(localMatch)} title={localMatch.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                      {localMatch.isActive ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                    <button className="page-btn-icon delete" onClick={() => setMatchToDelete(localMatch)} title="Löschen"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // List view
        <div className="page-data-panel">
          <table className="page-table">
            <thead>
              <tr>
                <th>Teams</th>
                <th>Ergebnis</th>
                <th>Spieltag</th>
                <th>Geplant</th>
                <th>Ort</th>
                <th>Schiedsrichter</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocalMatches.map((localMatch) => {
                const statusInfo = getLocalMatchStatusInfo(localMatch.status);
                return (
                  <tr key={localMatch.id}>
                    <td>
                      <CircleDot size={12} /> <strong>{formatLocalMatchTeams(localMatch)}</strong>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{localMatch.homeTeam} vs {localMatch.awayTeam}</div>
                    </td>
                    <td>
                      {formatLocalMatchResult(localMatch)}
                      {localMatch.winner !== 'TBD' && localMatch.status === LocalMatchStatus.Finished && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>Gewinner: {localMatch.winner}</div>
                      )}
                    </td>
                    <td>
                      <strong>{localMatch.localMatchday.name}</strong>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{formatDate(localMatch.localMatchday.date)}</div>
                    </td>
                    <td>
                      {formatDateTime(localMatch.scheduledDate)}
                      {localMatch.actualDate && (
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>Tatsächlich: {formatDateTime(localMatch.actualDate)}</div>
                      )}
                    </td>
                    <td>{localMatch.location || '-'}</td>
                    <td>{localMatch.referee || '-'}</td>
                    <td>
                      <span className="page-badge" style={{ backgroundColor: statusInfo.color }}>
                        {statusInfo.icon} {statusInfo.label}
                      </span>
                      {!localMatch.isActive && (
                        <span className="page-badge" style={{ backgroundColor: '#dc3545' }}>Inaktiv</span>
                      )}
                    </td>
                    <td>
                      <div className="page-table-actions">
                        <button className="page-btn-icon edit" onClick={() => handleEditMatch(localMatch)} title="Bearbeiten"><Pencil size={14} /></button>
                        <button className="page-btn-icon" onClick={() => handleToggleMatchActive(localMatch)} title={localMatch.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                          {localMatch.isActive ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button className="page-btn-icon delete" onClick={() => setMatchToDelete(localMatch)} title="Löschen"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

      {/* Match Form Modal */}
      {showMatchForm && (
        <div className="page-modal-overlay">
          <div className="page-modal-content">
            <LocalMatchForm
              match={editingMatch || undefined}
              matchdays={localMatchdays}
              onSave={handleSaveMatch}
              onCancel={() => {
                setShowMatchForm(false);
                setEditingMatch(null);
              }}
              loading={matchFormLoading}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!matchToDelete}
        title="Spiel löschen"
        message={`Möchten Sie das Spiel "${matchToDelete ? formatLocalMatchTeams(matchToDelete) : ''}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteMatch}
        onCancel={() => setMatchToDelete(null)}
        confirmText="Löschen"
        cancelText="Abbrechen"
        type="danger"
      />
    </div>
  );
};

export default LocalMatches;
